import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Megaphone, Handshake, Calendar, Heart, ArrowRight, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const volunteerInterests = [
  { id: 'events', label: 'Event Support', desc: 'Help at fundraisers and community events' },
  { id: 'coaching', label: 'Coaching/Mentoring', desc: 'Share your athletic expertise with youth' },
  { id: 'admin', label: 'Administrative', desc: 'Office support and organization' },
  { id: 'outreach', label: 'Community Outreach', desc: 'Spread awareness in your community' },
  { id: 'fundraising', label: 'Fundraising', desc: 'Help with donor outreach and campaigns' },
];

const ways = [
  {
    icon: Users,
    title: 'Volunteer',
    description: 'Give your time and talents to support young athletes in your community. From event support to mentoring, every hour makes a difference.',
    cta: 'Sign Up to Volunteer',
    link: '#volunteer-form',
  },
  {
    icon: Megaphone,
    title: 'Advocate',
    description: 'Be a voice for youth athletics equity. Share our mission with your network and help us reach more families in need.',
    cta: 'Become an Advocate',
    link: '#volunteer-form',
  },
  {
    icon: Handshake,
    title: 'Partner',
    description: 'Local businesses and organizations can partner with us through sponsorships, in-kind donations, or employee giving programs.',
    cta: 'Explore Partnerships',
    link: '/sponsors',
  },
  {
    icon: Calendar,
    title: 'Attend Events',
    description: 'Join us at fundraisers, community days, and athletic events. Your presence shows our athletes they have a community behind them.',
    cta: 'View Events',
    link: '/events',
  },
];

const GetInvolved = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interests: [],
    availability: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  // Spam defences: invisible to people, and a timestamp so the server can reject
  // submissions that arrive faster than anyone could type.
  const [honeypot, setHoneypot] = useState('');
  const formLoadedAt = useRef(Date.now());

  const handleInterestChange = (interestId, checked) => {
    setFormData(prev => ({
      ...prev,
      interests: checked
        ? [...prev.interests, interestId]
        : prev.interests.filter(i => i !== interestId),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      await axios.post(`${API}/volunteers`, {
        ...formData,
        website: honeypot,
        form_loaded_at: formLoadedAt.current,
      });
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        interests: [],
        availability: '',
        message: '',
      });
    } catch (error) {
      console.error('Volunteer signup error:', error);
      setSubmitStatus('error');
      if (error?.response?.status === 429 && error.response.data?.detail) {
        setErrorMessage(error.response.data.detail);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20" data-testid="get-involved-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-red-500 font-bold uppercase tracking-widest text-sm">Get Involved</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mt-4">
              Join the Movement
            </h1>
            <p className="mt-6 text-xl text-slate-300 max-w-2xl mx-auto">
              There are many ways to support young athletes beyond donations. Your time, voice, and connections can make a lasting impact.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Ways to Get Involved */}
      <section className="py-20 md:py-32 bg-white" data-testid="ways-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {ways.map((way, index) => (
              <motion.div
                key={way.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-8 border border-slate-200 hover:border-red-600 hover:shadow-lg transition-all"
                data-testid={`way-card-${index}`}
              >
                <way.icon className="w-12 h-12 text-red-600 mb-4" />
                <h3 className="font-heading text-2xl font-bold text-slate-900">{way.title}</h3>
                <p className="mt-3 text-slate-600">{way.description}</p>
                {way.link.startsWith('#') ? (
                  <a
                    href={way.link}
                    className="inline-flex items-center mt-6 text-red-600 font-bold uppercase tracking-wider text-sm hover:text-slate-900 transition-colors"
                  >
                    {way.cta}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                ) : (
                  <Link
                    to={way.link}
                    className="inline-flex items-center mt-6 text-red-600 font-bold uppercase tracking-wider text-sm hover:text-slate-900 transition-colors"
                  >
                    {way.cta}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Volunteer Form */}
      <section id="volunteer-form" className="py-20 md:py-32 bg-slate-50" data-testid="volunteer-form-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-red-600 font-bold uppercase tracking-widest text-sm">Volunteer</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-4">
              Sign Up to Volunteer
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Fill out the form below and we'll be in touch about opportunities that match your interests.
            </p>
          </div>

          {submitStatus === 'success' ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white p-12 border border-slate-200 text-center"
              data-testid="volunteer-success"
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-slate-900">Thank You!</h3>
              <p className="mt-4 text-slate-600">
                We've received your volunteer signup. A member of our team will be in touch soon about opportunities that match your interests.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white p-8 border border-slate-200" data-testid="volunteer-form">
              {/* Honeypot: bots fill every field they find; positioned offscreen
                  rather than display:none, which some bots deliberately skip. */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px' }}
                data-testid="volunteer-honeypot"
              />
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Full Name *</Label>
                  <Input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-1 border-slate-200"
                    data-testid="volunteer-name-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Email *</Label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="mt-1 border-slate-200"
                    data-testid="volunteer-email-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Phone</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="mt-1 border-slate-200"
                    data-testid="volunteer-phone-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Availability</Label>
                  <Input
                    type="text"
                    placeholder="e.g., Weekends, Evenings"
                    value={formData.availability}
                    onChange={(e) => setFormData(prev => ({ ...prev, availability: e.target.value }))}
                    className="mt-1 border-slate-200"
                    data-testid="volunteer-availability-input"
                  />
                </div>
              </div>

              <div className="mb-6">
                <Label className="text-sm font-medium text-slate-700 mb-3 block">Areas of Interest</Label>
                <div className="space-y-3">
                  {volunteerInterests.map((interest) => (
                    <div key={interest.id} className="flex items-start space-x-3 p-3 bg-slate-50 border border-slate-100">
                      <Checkbox
                        id={interest.id}
                        checked={formData.interests.includes(interest.id)}
                        onCheckedChange={(checked) => handleInterestChange(interest.id, checked)}
                        data-testid={`interest-${interest.id}`}
                      />
                      <div className="flex-1">
                        <Label htmlFor={interest.id} className="font-medium text-slate-900 cursor-pointer">
                          {interest.label}
                        </Label>
                        <p className="text-sm text-slate-600">{interest.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <Label className="text-sm font-medium text-slate-700">Additional Message</Label>
                <Textarea
                  rows={4}
                  placeholder="Tell us more about yourself or any questions you have..."
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  className="mt-1 border-slate-200"
                  data-testid="volunteer-message-input"
                />
              </div>

              {submitStatus === 'error' && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="volunteer-error">
                  {errorMessage || 'Something went wrong. Please try again.'}
                </div>
              )}

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-red-600 text-white hover:bg-slate-900 rounded-none uppercase tracking-widest font-bold py-4"
                data-testid="volunteer-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Sign Up to Volunteer
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-24 bg-red-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            Prefer to Donate?
          </h2>
          <p className="mt-4 text-xl text-red-100 max-w-2xl mx-auto">
            Financial contributions help us reach even more young athletes in need.
          </p>
          <div className="mt-8">
            <Link to="/donate" data-testid="involved-donate-btn">
              <Button className="bg-white text-red-600 hover:bg-slate-900 hover:text-white rounded-none uppercase tracking-widest font-bold px-10 py-6 text-base">
                <Heart className="mr-2 w-5 h-5" />
                Make a Donation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GetInvolved;
